import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Modal, Typeahead } from '@gsa/afp-component-library';
import {
  GET_VENDOR_BY_UEI_TYPEAHEAD,
  UPDATE_CONTRACT_HEADER_WITH_NEW_VENDOR,
} from '../../../services/data-store.gql';
import { GET_CONTRACT_HEADER_BY_ID } from './contract-header.gql';

const ContractHeaderUpdateUeiModal = ({
  onComplete,
  closeModal,
  disabled = false,
  currentVendor,
  onReviewDetailsClick,
}) => {
  const [option, setOption] = useState({ values: [], raw: [] });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [userInput, setUserInput] = useState('');
  const { contractHeaderId } = useParams();

  const [getVendorByUEITypeAhead] = useLazyQuery(GET_VENDOR_BY_UEI_TYPEAHEAD, {
    onCompleted: (data) => {
      const vendorData = data.getVendorByUEITypeAhead;
      if (vendorData) {
        // Handle both single vendor and array of vendors
        const vendors = Array.isArray(vendorData) ? vendorData : [vendorData];
        setOption({
          values: vendors.map((v) => `${v.vendorName} - ${v.vendorUEI}`),
          raw: vendors,
        });
      } else {
        setOption({ values: [], raw: [] });
      }
    },
    onError: () => {
      setOption({ values: [], raw: [] });
    },
  });

  const [updateContractHeaderWithNewVendor, { loading: updateLoading }] =
    useMutation(UPDATE_CONTRACT_HEADER_WITH_NEW_VENDOR, {
      refetchQueries: [
        {
          query: GET_CONTRACT_HEADER_BY_ID,
          variables: {
            contractHeaderId,
          },
        },
      ],
      onCompleted: (data) => {
        if (data.updateContractHeaderWithNewVendor.success === false) {
          const errorResult = {
            success: false,
            error: true,
            message:
              "The Vehicle supplier's vendor company associated with the contract has not been updated.",
            reviewDetailsText: 'Review details',
            data: data.updateContractHeaderWithNewVendor,
            differences: data.updateContractHeaderWithNewVendor.differences,
            onReviewDetailsClick: onReviewDetailsClick,
          };
          onComplete(errorResult);
          return;
        }
        // Close modal and propagate success message to parent
        onComplete({
          success: true,
          message: `The Vehicle supplier associated with the contract has been updated to ${selectedVendor.vendorName}`,
          data: data.updateContractHeaderWithNewVendor,
        });
      },
      onError: (error) => {
        onComplete({
          success: false,
          error: true,
          message:
            'An error occurred while updating the contract header. Please try again.',
          data: { error: error.message },
        });
      },
    });

  const handleSave = () => {
    if (selectedVendor) {
      updateContractHeaderWithNewVendor({
        variables: {
          contractHeaderId: contractHeaderId,
          vendorId: selectedVendor.vendorId,
        },
      });
    }
  };

  return (
    <Modal
      title={<h2>Edit vehicle supplier's UEI</h2>}
      id="contract-header-update-uei-modal"
      variant="large"
      className="solicitation-modal"
      onClose={closeModal}
      actions={
        <>
          <Button
            type="button"
            variant="unstyled"
            onClick={closeModal}
            label="Cancel"
            disabled={disabled}
          />
          <Button
            data-testid="modal-save-changes-button"
            type="button"
            onClick={handleSave}
            label="Save changes"
            disabled={disabled || !selectedVendor || updateLoading}
          />
        </>
      }
    >
      <div className="margin-bottom-2">
        <p>
          Select the UEI to update the contract header. This update will
          unpublish the contract.
        </p>
      </div>

      {/* Current Vendor Information */}
      {currentVendor && (
        <div className="margin-bottom-3 padding-2 bg-base-lighter">
          <h3 className="margin-top-0 margin-bottom-2">
            Current vehicle supplier
          </h3>
          <div className="grid-row grid-gap">
            <div className="grid-col-4">
              <strong>Vendor Name:</strong>
              <br />
              {currentVendor.vendorName || 'N/A'}
            </div>
            <div className="grid-col-4">
              <strong>UEI Number:</strong>
              <br />
              {currentVendor.uniqueEntityIdentifier || 'N/A'}
            </div>
            <div className="grid-col-4">
              <strong>Fleet Vendor Number:</strong>
              <br />
              {currentVendor.fleetVendorNo || 'N/A'}
            </div>
          </div>
        </div>
      )}

      <div className="margin-top-1">
        <Typeahead
          id="uei-typeahead"
          data-testid="uei-typeahead"
          name="UEI"
          placeholder="Type at least 3 characters to search for UEI..."
          filterValue={
            selectedVendor
              ? `${selectedVendor.vendorName} - ${selectedVendor.vendorUEI}`
              : ''
          }
          typeaheadValues={option?.values}
          onFilterKeyDown={(e) => {
            setUserInput(e.target.value);
          }}
          onOptionEnter={(selected) => {
            // Find the selected vendor from the raw data
            const selectedVendorData = option?.raw.find(
              (v) => `${v.vendorName} - ${v.vendorUEI}` === selected,
            );
            setSelectedVendor(selectedVendorData);
          }}
          onClear={() => {
            setSelectedVendor(null);
            setUserInput('');
          }}
          fetchTypeaheadValues={(_, search) => {
            const keyword = search.trim();
            if (keyword?.length > 0) {
              getVendorByUEITypeAhead({
                variables: {
                  uei: keyword,
                  vendorId: currentVendor?.id,
                },
              });
            } else {
              setOption({ values: [], raw: [] });
            }
          }}
          debounceDelay={250}
          inputCharNum={3}
        />
      </div>

      {/* Selected Vendor Preview */}
      {selectedVendor && (
        <div className="margin-top-2 padding-2 bg-green-5">
          <h4 className="margin-top-0 margin-bottom-2">
            Selected new vehicle supplier
          </h4>
          <div className="grid-row grid-gap">
            <div className="grid-col-4">
              <strong>Vendor Name:</strong>
              <br />
              {selectedVendor.vendorName}
            </div>
            <div className="grid-col-4">
              <strong>UEI Number:</strong>
              <br />
              {selectedVendor.vendorUEI}
            </div>
            <div className="grid-col-4">
              <strong>Fleet Vendor Number:</strong>
              <br />
              {selectedVendor.fleetVendorNo}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

ContractHeaderUpdateUeiModal.propTypes = {
  onComplete: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  currentVendor: PropTypes.object,
  onReviewDetailsClick: PropTypes.func,
};

export default ContractHeaderUpdateUeiModal;
